// =============================================================
//  AddAdmin — Création d'un compte administrateur (formulaire)
// =============================================================

import React, { useState } from 'react';
import { View, Text, StyleSheet, Alert } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import {
  getAuth,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';

import AdminLayout from './web-ui/AdminLayout';
import { Card, Button, Badge, Input, Divider, SegmentedSelect } from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const AddAdmin = () => {
  const navigation = useNavigation();
  const auth = getAuth();
  const db = getFirestore();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [adminLevel, setAdminLevel] = useState('level2');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!fullName.trim()) e.fullName = 'Le nom est requis';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Email invalide';
    if (phone && !/^\d{9}$/.test(phone)) e.phone = 'Numéro invalide (9 chiffres)';
    if (password.length < 6 || password.length > 9)
      e.password = 'Mot de passe : 6 à 9 caractères';
    if (password !== confirmPassword) e.confirmPassword = 'Les mots de passe ne correspondent pas';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'admin', cred.user.uid), {
        fullName,
        email: email.toLowerCase(),
        phone: phone ? `+237${phone}` : '',
        status: 'active',
        userType: 'admin',
        adminLevel,
        isVerified: true,
        createdAt: serverTimestamp(),
        adminId: cred.user.uid,
        uid: cred.user.uid,
      });
      Alert.alert('Succès', 'Compte administrateur créé avec succès !');
      navigation.goBack();
    } catch (err) {
      console.log('add admin error', err?.message || err);
      let msg = "Erreur lors de l'inscription";
      if (err.code === 'auth/email-already-in-use') msg = 'Cet email est déjà utilisé';
      else if (!navigator.onLine) msg = 'Pas de connexion internet';
      Alert.alert('Erreur', msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout
      title="Nouvel administrateur"
      subtitle="Créer un compte admin avec les droits associés"
    >
      <View style={styles.actions}>
        <Button variant="secondary" icon="arrow-left" onPress={() => navigation.goBack()}>
          Retour
        </Button>
      </View>

      <View style={styles.row}>
        <Card style={{ flex: 2, minWidth: 320, gap: spacing.md }}>
          <Text style={styles.section}>Informations</Text>
          <Field label="Nom complet" error={errors.fullName}>
            <Input icon="account-outline" value={fullName} onChangeText={setFullName} placeholder="Nom et prénom" />
          </Field>
          <Field label="Email" error={errors.email}>
            <Input
              icon="email-outline"
              value={email}
              onChangeText={setEmail}
              placeholder="admin@exemple.com"
              type="email"
            />
          </Field>
          <Field label="Téléphone (optionnel)" error={errors.phone}>
            <Input
              icon="phone-outline"
              value={phone}
              onChangeText={setPhone}
              placeholder="6XXXXXXXX"
              type="number"
            />
          </Field>

          <Divider />

          <Text style={styles.section}>Sécurité</Text>
          <Field label="Mot de passe" error={errors.password}>
            <Input
              icon="lock-outline"
              value={password}
              onChangeText={setPassword}
              placeholder="6 à 9 caractères"
              type={showPassword ? 'text' : 'password'}
              rightIcon={showPassword ? 'eye-off-outline' : 'eye-outline'}
              onRightIconPress={() => setShowPassword((v) => !v)}
            />
          </Field>
          <Field label="Confirmer le mot de passe" error={errors.confirmPassword}>
            <Input
              icon="lock-check-outline"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              placeholder="Retaper le mot de passe"
              type={showConfirm ? 'text' : 'password'}
              rightIcon={showConfirm ? 'eye-off-outline' : 'eye-outline'}
              onRightIconPress={() => setShowConfirm((v) => !v)}
            />
          </Field>
        </Card>

        <Card style={{ flex: 1, minWidth: 280, gap: spacing.md, height: 'fit-content' }}>
          <Text style={styles.section}>Niveau d'accès</Text>
          <View style={{ gap: 6 }}>
            <SegmentedSelect
              value={adminLevel}
              onChange={setAdminLevel}
              options={[
                { value: 'level1', label: 'Niveau 1 (Super)' },
                { value: 'level2', label: 'Niveau 2' },
              ]}
            />
          </View>
          <Badge variant={adminLevel === 'level1' ? 'primary' : 'info'}>
            {adminLevel === 'level1' ? 'Super administrateur' : 'Administrateur'}
          </Badge>
          <Text style={styles.helper}>
            {adminLevel === 'level1'
              ? 'Accès complet : peut créer d\'autres admins.'
              : 'Accès gestion : pas le droit de créer d\'autres admins.'}
          </Text>

          <Divider />

          <Button
            icon="account-plus-outline"
            loading={loading}
            onPress={handleSubmit}
            fullWidth
          >
            Créer l'administrateur
          </Button>
        </Card>
      </View>
    </AdminLayout>
  );
};

function Field({ label, error, children }) {
  return (
    <View style={{ gap: 6 }}>
      <Text style={styles.label}>{label}</Text>
      {children}
      {!!error && <Text style={styles.error}>{error}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  actions: { flexDirection: 'row', justifyContent: 'flex-start', marginBottom: spacing.md },
  row: { flexDirection: 'row', gap: spacing.lg, flexWrap: 'wrap', alignItems: 'flex-start' },
  section: { fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: 4 },
  label: { fontSize: 12, fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3 },
  helper: { fontSize: 12, color: colors.textSecondary, lineHeight: 17 },
  error: { fontSize: 12, color: colors.danger, marginTop: 2 },
});

export default AddAdmin;
